import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { apiJson, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

type Step = 'email' | 'code';

export function LoginPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('to') ?? '/';

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Compute a FingerprintJS-OSS visitorId in the background as soon as the
  // page loads. Stored in a ref so we don't trigger re-renders. The verify
  // call submits it for the access-monitoring audit view; auth decisions
  // never depend on it.
  const fingerprintRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    FingerprintJS.load()
      .then((fp) => fp.get())
      .then((result) => {
        if (!cancelled) fingerprintRef.current = result.visitorId;
      })
      .catch(() => {
        // ignore — fingerprinting is best-effort
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await apiJson('/api/auth/otp/request', 'POST', { email: email.trim().toLowerCase() });
      setStep('code');
      setInfo(
        `If ${email.trim().toLowerCase()} is allowed to access Pulse, a 6-digit code has been emailed.`,
      );
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await apiJson('/api/auth/otp/verify', 'POST', {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        ...(fingerprintRef.current ? { fingerprint: fingerprintRef.current } : {}),
      });
      await refresh();
      navigate(returnTo, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 410) setErr('That code has expired. Request a fresh one.');
        else if (e.status === 429) setErr('Too many attempts on this code. Request a fresh one.');
        else setErr('Invalid code.');
      } else {
        setErr(String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cockpit-bg text-cockpit-text">
      <main className="w-full max-w-sm space-y-4 rounded-lg border border-cockpit-border bg-cockpit-panel p-6">
        <header>
          <h1 className="text-lg font-semibold">QEA Pulse</h1>
          <p className="text-xs text-cockpit-dim">cockpit · sign in</p>
        </header>

        {step === 'email' && (
          <form onSubmit={submitEmail} className="space-y-3">
            <label className="block">
              <span className="text-xs text-cockpit-dim">Email</span>
              <input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded border border-cockpit-border bg-cockpit-bg px-3 py-2 text-sm text-cockpit-text"
                placeholder="you@insightdelivered.com"
                autoComplete="email"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !email}
              className="w-full rounded bg-cockpit-text px-3 py-2 text-sm font-medium text-cockpit-bg disabled:opacity-40"
            >
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
            <p className="text-xs text-cockpit-dim">
              Access is restricted. Ask an admin to add your email if you can&apos;t log in.
            </p>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} className="space-y-3">
            <p className="text-xs text-cockpit-dim">
              Code sent to <code className="text-cockpit-text">{email}</code>. Valid for 5 min.
            </p>
            <label className="block">
              <span className="text-xs text-cockpit-dim">6-digit code</span>
              <input
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 w-full rounded border border-cockpit-border bg-cockpit-bg px-3 py-2 text-center font-mono text-2xl tracking-[0.5em] text-cockpit-text"
                placeholder="• • • • • •"
                autoComplete="one-time-code"
              />
            </label>
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full rounded bg-cockpit-text px-3 py-2 text-sm font-medium text-cockpit-bg disabled:opacity-40"
            >
              {busy ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
                setErr(null);
                setInfo(null);
              }}
              className="w-full text-xs text-cockpit-dim hover:text-cockpit-text"
            >
              ← Use a different email
            </button>
          </form>
        )}

        {info && (
          <div className="rounded border border-cockpit-ok bg-cockpit-ok/10 p-2 text-xs text-cockpit-ok">
            {info}
          </div>
        )}
        {err && (
          <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
            {err}
          </div>
        )}
      </main>
    </div>
  );
}
